/**
 * Pure timezone-picker helpers (WSRADM-0047). Offsets come from `Intl`, never from
 * a hard-coded table (UI standard §27).
 */

const offsetCache = new Map<string, string>();

/**
 * `"UTC±HH:MM"` for a zone right now, from `Intl` `longOffset` (`GMT±HH:MM` →
 * `UTC±HH:MM`; plain `GMT` → `UTC±00:00`). Returns `''` for a zone `Intl`
 * rejects.
 */
export function utcOffset(zone: string): string {
  const cached = offsetCache.get(zone);
  if (cached !== undefined) return cached;
  let offset = '';
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'longOffset',
    }).formatToParts(new Date());
    const raw = parts.find((part) => part.type === 'timeZoneName')?.value ?? '';
    if (raw === 'GMT') offset = 'UTC±00:00';
    else if (raw.startsWith('GMT')) offset = `UTC${raw.slice(3)}`;
  } catch {
    offset = '';
  }
  offsetCache.set(zone, offset);
  return offset;
}

/** `"{zone} (UTC±HH:MM)"`, or the bare zone when `Intl` has no offset for it. */
export function zoneLabel(zone: string): string {
  const offset = utcOffset(zone);
  return offset === '' ? zone : `${zone} (${offset})`;
}

/** The runtime's IANA zones, or `null` when `Intl.supportedValuesOf` is unavailable. */
export function supportedZones(): string[] | null {
  const intl = Intl as typeof Intl & { supportedValuesOf?: (key: string) => string[] };
  if (typeof intl.supportedValuesOf !== 'function') return null;
  try {
    return intl.supportedValuesOf('timeZone');
  } catch {
    return null;
  }
}

/** The browser's own zone, or `null` when it cannot be resolved. */
export function browserZone(): string | null {
  try {
    const zone = new Intl.DateTimeFormat().resolvedOptions().timeZone;
    return typeof zone === 'string' && zone.length > 0 ? zone : null;
  } catch {
    return null;
  }
}

/**
 * Picker options: the runtime's zones plus `UTC` (some engines omit it) and any
 * `extra` zones that must be selectable (the saved zone, the browser zone),
 * de-duplicated and sorted.
 */
export function zoneOptions(zones: readonly string[], extra: readonly (string | null)[]): string[] {
  const set = new Set<string>(zones);
  set.add('UTC');
  for (const zone of extra) if (zone) set.add(zone);
  return [...set].sort((a, b) => a.localeCompare(b));
}
